import { defineStore } from 'pinia'
import { store } from '../index'
import { UserLoginType, UserType } from '@/api/login/types'
import { ElMessageBox } from 'element-plus'
import { useI18n } from '@/hooks/web/useI18n'
import { useTagsViewStore } from './tagsView'

import { logout } from '@/api/controller/extra'

import router, { resetRouter } from '@/router'
import { usePermissionStoreWithOut } from './permission'
import { useMenuStore } from './menu'

interface UserState {
  userInfo?: UserType
  tokenKey: string
  token: string
  schoolName: string
  schoolId: number | string
  roleRouters?: string[] | AppCustomRouteRecordRaw[]
  rememberMe: boolean
  loginInfo?: UserLoginType
  domain: string
  teacherId: string
}

// 本地缓存 key
const TOKEN_KEY = 'HYschoolToken'
const SCHOOL_NAME_KEY = 'HYschoolName'
const SCHOOL_ID_KEY = 'HYschoolId'

// 安全读取本地缓存
const getLocal = (key: string): string => {
  return localStorage.getItem(key) ?? ''
}

// 安全设置本地缓存
const setLocal = (key: string, value: string) => {
  localStorage.setItem(key, value)
}

// 安全删除本地缓存
const removeLocal = (key: string) => {
  localStorage.removeItem(key)
}

export const useUserStore = defineStore('user', {
  state: (): UserState => {
    return {
      userInfo: undefined,
      tokenKey: 'Authorization',
      token: getLocal(TOKEN_KEY),
      roleRouters: undefined,
      schoolName: getLocal(SCHOOL_NAME_KEY),
      schoolId: getLocal(SCHOOL_ID_KEY),
      // 记住我
      rememberMe: true,
      loginInfo: undefined,
      domain: '',
      teacherId: ''
    }
  },

  getters: {
    isLogin(state) {
      return !!state.token && !!state.userInfo?.id
    },

    getTokenKey(): string {
      return this.tokenKey
    },

    getToken(): string {
      return this.token || getLocal(TOKEN_KEY)
    },

    getUserInfo(): UserType | undefined {
      return this.userInfo
    },

    getRoleRouters(): string[] | AppCustomRouteRecordRaw[] | undefined {
      return this.roleRouters
    },

    getRememberMe(): boolean {
      return this.rememberMe
    },

    getLoginInfo(): UserLoginType | undefined {
      return this.loginInfo
    },

    getSchoolName(): string {
      return this.schoolName || getLocal(SCHOOL_NAME_KEY)
    },

    getSchoolId(): string | number {
      return this.schoolId || getLocal(SCHOOL_ID_KEY)
    }
  },

  actions: {
    async initDomain() {
      try {
        const baseUrl = import.meta.env.BASE_URL || '/'
        const configUrl = `${baseUrl.replace(/\/?$/, '/')}config.json`

        const res = await fetch(configUrl, {
          cache: 'no-store'
        })

        if (!res.ok) {
          return
        }

        const data = await res.json()
        this.domain = data?.domain || ''
      } catch (error) {
        console.error('读取 config.json 失败：', error)
      }
    },

    setTokenKey(tokenKey: string) {
      this.tokenKey = tokenKey
    },

    setToken(token: string) {
      this.token = token
      if (token) {
        setLocal(TOKEN_KEY, token)
      } else {
        removeLocal(TOKEN_KEY)
      }
    },

    setUserInfo(userInfo?: UserType) {
      this.userInfo = userInfo
    },

    setSchoolName(schoolName: string) {
      this.schoolName = schoolName
      if (schoolName) {
        setLocal(SCHOOL_NAME_KEY, schoolName)
      } else {
        removeLocal(SCHOOL_NAME_KEY)
      }
    },

    setSchoolId(schoolId: string | number) {
      this.schoolId = schoolId
      if (schoolId !== '' && schoolId !== null && schoolId !== undefined) {
        setLocal(SCHOOL_ID_KEY, String(schoolId))
      } else {
        removeLocal(SCHOOL_ID_KEY)
      }
    },

    setRoleRouters(roleRouters: string[] | AppCustomRouteRecordRaw[]) {
      this.roleRouters = roleRouters
    },

    logoutConfirm() {
      const { t } = useI18n()
      ElMessageBox.confirm(t('common.loginOutMessage'), t('common.reminder'), {
        confirmButtonText: t('common.ok'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      })
        .then(async () => {
          await logout().catch(() => {})
          this.reset()
        })
        .catch(() => {})
    },

    reset() {
      const tagsViewStore = useTagsViewStore()
      const permissionStore = usePermissionStoreWithOut()
      const menuStore = useMenuStore()

      // 1. 清理标签页
      tagsViewStore.delAllViews()

      // 2. 先清掉动态路由，避免旧账号路由残留
      resetRouter()

      // 3. 清理权限路由状态
      if (typeof permissionStore.resetPermissionState === 'function') {
        permissionStore.resetPermissionState()
      } else {
        permissionStore.setIsAddRouters(false)
        ;(permissionStore as any).addRouters = []
        ;(permissionStore as any).routers = []
        ;(permissionStore as any).menuTabRouters = []
      }

      // 4. 清理菜单当前页状态
      menuStore.setPage('')

      // 5. 清理当前用户状态
      this.setToken('')
      this.setUserInfo(undefined)
      this.setSchoolName('')
      this.setSchoolId('')
      this.setRoleRouters([])

      // 6. 清理本地缓存
      removeLocal(TOKEN_KEY)
      removeLocal(SCHOOL_NAME_KEY)
      removeLocal(SCHOOL_ID_KEY)

      // 7. 跳转登录页
      router.replace('/login')
    },

    logout() {
      this.reset()
    },

    setRememberMe(rememberMe: boolean) {
      this.rememberMe = rememberMe
    },

    setLoginInfo(loginInfo: UserLoginType | undefined) {
      this.loginInfo = loginInfo
    }
  },

  persist: true
})

export const useUserStoreWithOut = () => {
  return useUserStore(store)
}
